import React, { useState, useEffect, createRef } from 'react'
import styles from './ProductInfoForm.module.scss'
import { inject } from 'mobx-react'
import { BRAND_STORE, PRODUCT_STORE, GENERAL_STORE } from 'store'
import {
  LabeledDropdown, Tag, IconButton, LabeledInput, LabeledTextarea,
  Button, LabeledGalleryDropZone, RelatedMediaFormSection
} from 'components'
import SkusFormSection from './SkusFormSection'
import { icons } from 'assets'
import { observer } from 'mobx-react-lite'
import { Props, newProduct, StateProduct } from './types'
import ShippingsSection from './ShippingsSection'
import { Product, Sku } from 'models'

const ProductInfoForm = (props: Props) => {
  const [isScrollOnTop, setIsScrollOnTop] = useState(true)
  const [formState, setFormState] = useState(newProduct)
  let isSubmitWithEnterKeyAllowed = true
  const imagesRef: React.RefObject<{
    handleImages: () => Promise<any>;
    getImages: () => any[];
  }> = createRef()

  const {
    [BRAND_STORE]: { brandsList, getBrands },
    [PRODUCT_STORE]: { createProduct, editProduct },
    [GENERAL_STORE]: { setLoadingOn, showError, push },
    product
  } = props
  const creatingANewProduct = !product

  useEffect(() => {
    if (creatingANewProduct) {
      const needToLoadBrandsList = !brandsList.length
      needToLoadBrandsList && getBrands()
    } else {
      const canFeedProductDetails = JSON.stringify(product) !== '{}'
      canFeedProductDetails &&
        setFormState({
          ...product,
          tags: product.tags ? product.tags.split(',') : []
        } as StateProduct)
    }
  }, [brandsList, getBrands, product, creatingANewProduct])

  const {
    name,
    brandId,
    brandName,
    tags,
    info,
    images,
    relatedMedia
  } = formState

  const handleOnScroll = e => {
    const isScrollNowOnTop = e.currentTarget.scrollTop === 0
    if (isScrollOnTop !== isScrollNowOnTop) setIsScrollOnTop(isScrollNowOnTop)
  }

  const handleTagInput = tag => {
    tag = tag.trim().toLocaleLowerCase()

    if (tags.includes(tag)) return

    setFormState({ ...formState, tags: [...tags, tag] })
  }

  const handleBrandSelect = ([{ id, name: bName }]) => {
    setFormState({ ...formState, brandId: id, brandName: bName })
  }

  const setProductProp = value => {
    setFormState({ ...formState, ...value })
  }

  const handleSubmit = () => {
    if (!isSubmitWithEnterKeyAllowed) return

    setLoadingOn()

    imagesRef.current.handleImages()
      .then(() => {
        const productDetails = {
          ...formState,
          tags: formState.tags.join(),
          images: imagesRef.current.getImages()
        } as Product

        product
          ? editProduct(productDetails)
          : createProduct(productDetails)
      })
      .catch(err => showError(err.message))
  }

  const additionalProps = {
    skus: formState.productSkus,
    setSkus: (productSkus: Sku[]) => setProductProp({ productSkus })
  }

  return (
    <div className={styles.container}>

      <h1 className={!isScrollOnTop ? styles.overEffect : ''}>
        {product ? `Edit ${product.name || '  '}` : 'Add a New Product'}
      </h1>
      <form onScroll={handleOnScroll}>
        <div>
          <LabeledDropdown
            label='Brand'
            placeholder='Select the brand of the product'
            options={creatingANewProduct ? brandsList : []}
            onSelect={handleBrandSelect}
            selectedOptions={[{ id: brandId, name: brandName }]}
          />
          <LabeledInput
            label='Product Name'
            placeholder='Type the name of the product'
            onChange={value => setFormState({ ...formState, name: value })}
            value={name}
          />
          <LabeledInput
            label='Tags'
            placeholder='Type a tag for the product, press enter to set a tag'
            keysToHandle={['Enter']}
            onKeyDown={handleTagInput}
            onFocus={() => { isSubmitWithEnterKeyAllowed = false }}
            onBlur={() => { isSubmitWithEnterKeyAllowed = true }}
          />
          <div className={styles.tagsContainer}>
            {tags.map((tag, index) =>
              <Tag key={index} tag={tag}>
                <IconButton
                  icon={icons.closeX}
                  className={styles.tagIcon}
                  onClick={() => setFormState({
                    ...formState,
                    tags: tags.filter(t => t !== tag)
                  })}
                />
              </Tag>
            )}
          </div>
        </div>

        <div>
          <LabeledTextarea
            label='Info'
            placeholder='Enter information about the product here'
            onChange={value => setFormState({ ...formState, info: value })}
            value={info}
          />
        </div>

        <LabeledGalleryDropZone
          ref={imagesRef}
          initialImages={images}
          additionalProps={additionalProps}
        />

        <ShippingsSection shippings={product.shippings} />

        <SkusFormSection
          product={formState}
          setProductProp={setProductProp}
          readOnly
          additionalProps={additionalProps}
        />

        <RelatedMediaFormSection
          selectedProjectsList={relatedMedia || []}
          // projectsList={projectsList}
          // getProjects={getProjects}
          setSelectedProjectsList={projects =>
            setFormState({ ...formState, relatedMedia: projects })
          }
        />

        <div className={styles.buttonsContainer}>
          <Button
            type='cancel'
            onClick={() => push('/products')}
          >
            Discard Changes
          </Button>
          <Button
            type='submit'
            className={styles.saveBttn}
            onClick={handleSubmit}
          >
            Save Changes
          </Button>
        </div>

      </form>

    </div>
  )
}

export default inject(BRAND_STORE, PRODUCT_STORE, GENERAL_STORE)(observer(ProductInfoForm))
